import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Geometry, MultiPolygon, Polygon } from 'geojson';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Plus, Sparkles, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import ActionDrawer from 'components/IAP/ActionDrawer';
import ActionViewModal from 'components/IAP/ActionViewModal';
import ActionsWorkspace, {
  type ActionsWorkspaceToolbarItem,
} from 'components/IAP/ActionsWorkspace';
import { INCIDENT, ORGANIZATION } from 'components/IAP/ICSConstants';
import {
  keys as incidentActionKeys,
  openInstantiatedIncidentTask,
  useIncidentActions,
  useInstantiateIncidentTask,
} from 'components/IAP/hooks/incidentActionHooks';
import {
  keys as orgActionKeys,
  useOrgActions,
} from 'components/IAP/hooks/orgActionHooks';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import { isAreaGeometry } from 'components/IAP/utils/actionSpatialFilter';
import {
  isSitrepAILockedError,
  useGenerateSectionTasks,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { resolveSitrepDefaultSpatialFilter } from 'components/Reports/utils/resolveSitrepSpatialFilter';
import type { TaskSectionSnapshot } from 'components/Reports/section/dynamicSectionSnapshot';
import { useAorDescendants, useAors } from 'hooks/useAors';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  readTaskSectionFilter,
  resolveTaskTables,
  selectSectionTasks,
  type TaskTable,
} from './taskSectionFilter';
import {
  sectionTaskLinkKeys,
  usePinSectionTasks,
  useSectionTaskLinks,
  useUnlinkSectionTask,
  type SectionTaskLink,
} from './useSectionTaskLinks';

type ReportTasksSectionLike = {
  id: string;
  name?: string | null;
  output_format?: string | null;
  section_template_id?: string | null;
  output_format_structured?: Record<string, unknown> | null;
};

type ReportTasksSitrep = Pick<GetSitrepsResponse, 'aor_id' | 'incident_id'> &
  Partial<Pick<GetSitrepsResponse, 'spatial_filter' | 'scope_source'>>;

type ReportTasksSectionProps = {
  sitrepId: string;
  section: ReportTasksSectionLike;
  sitrep?: ReportTasksSitrep | null;
  /** Edit tasks and which tasks belong to the section. */
  canEdit?: boolean;
  /** Offer AI generation (draft editing only). */
  allowGenerate?: boolean;
  isOpord?: boolean;
  /** Frozen rows from submission or approval. When set, nothing is live. */
  snapshot?: TaskSectionSnapshot | null;
  /** Changes when the section is regenerated, which can create tasks. */
  refreshKey?: string | null;
  className?: string;
};

const HIDDEN_TOOLBAR: readonly ActionsWorkspaceToolbarItem[] = [
  'summary',
  'mine',
  'approval',
  'assignee',
];

const TABLE_TITLES: Record<TaskTable, string> = {
  incident: 'Incident tasks',
  org: 'Org tasks',
};

function errorMessage(error: unknown, fallback: string): string {
  const message = (
    error as { response?: { data?: { error?: { message?: string } } } }
  )?.response?.data?.error?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/**
 * A report's Tasks section: the Hub's own task table, narrowed to the tasks
 * this section is about. Edits here are edits to the real tasks, so the Hub
 * Tasks tab and the report always agree.
 */
export default function ReportTasksSection({
  sitrepId,
  section,
  sitrep,
  canEdit = false,
  allowGenerate = false,
  isOpord = false,
  snapshot,
  refreshKey,
  className,
}: ReportTasksSectionProps) {
  const hwcg = useHwcgFlag();
  const queryClient = useQueryClient();
  const incidentId = sitrep?.incident_id ?? null;
  const frozen = !!snapshot;
  const editable = canEdit && !frozen;

  const filter = useMemo(
    () => readTaskSectionFilter(section.output_format_structured, { isOpord }),
    [isOpord, section.output_format_structured]
  );
  // A saved snapshot keeps the tables it was taken with, even if the filter changed since.
  const tables = useMemo(() => {
    const current = resolveTaskTables(filter.scope, incidentId);
    if (!snapshot) return current;
    const saved = (['incident', 'org'] as const).filter(
      (table) => snapshot[table].length > 0
    );
    return saved.length > 0 ? saved : current;
  }, [filter.scope, incidentId, snapshot]);
  const wantsOrg = tables.includes('org') && !frozen;
  const wantsIncident = tables.includes('incident') && !frozen;

  const orgQuery = useOrgActions(wantsOrg ? ORGANIZATION : INCIDENT);
  const incidentQuery = useIncidentActions(
    wantsIncident ? INCIDENT : ORGANIZATION,
    incidentId
  );
  const linksQuery = useSectionTaskLinks(
    frozen ? undefined : sitrepId,
    section.id
  );
  const links: Array<Pick<SectionTaskLink, 'action_id' | 'source'>> = useMemo(
    () => (snapshot ? snapshot.links : (linksQuery.data ?? [])),
    [linksQuery.data, snapshot]
  );
  const linkByActionId = useMemo(
    () => new Map(links.map((link) => [link.action_id, link])),
    [links]
  );
  const linkedActionIds = useMemo(
    () => new Set(linkByActionId.keys()),
    [linkByActionId]
  );

  const { data: aors = [] } = useAors();
  const descendantsQuery = useAorDescendants(
    !frozen && filter.area !== 'none' ? sitrep?.aor_id : null
  );
  const reportAorId = sitrep?.aor_id ?? null;
  const aorIds = useMemo(() => {
    if (!reportAorId) return null;
    const ids = new Set<string>([reportAorId]);
    for (const row of descendantsQuery.data ?? []) ids.add(row.id);
    return ids;
  }, [descendantsQuery.data, reportAorId]);
  // Same order as the server snapshot: the saved report area, then the AOR.
  const areaGeometry = useMemo((): Polygon | MultiPolygon | null => {
    if (!sitrep) return null;
    const saved = sitrep.spatial_filter as Geometry | null | undefined;
    if (isAreaGeometry(saved)) return saved;
    const aor = sitrep.aor_id
      ? aors.find((item) => item.id === sitrep.aor_id)
      : null;
    return (
      resolveSitrepDefaultSpatialFilter({
        sitrep: { aor_id: sitrep.aor_id, incident_id: null },
        aor,
      })?.spatialFilter ?? null
    );
  }, [aors, sitrep]);

  const selected = useMemo(() => {
    if (snapshot) {
      return {
        org: snapshot.org as unknown as ActionEntity[],
        incident: snapshot.incident as unknown as ActionEntity[],
      };
    }
    return selectSectionTasks<ActionEntity>({
      org: (orgQuery.data ?? []) as ActionEntity[],
      incident: (incidentQuery.data ?? []) as ActionEntity[],
      filter,
      incidentId,
      areaGeometry,
      aorIds,
      linkedActionIds,
    });
  }, [
    aorIds,
    areaGeometry,
    filter,
    incidentId,
    incidentQuery.data,
    linkedActionIds,
    orgQuery.data,
    snapshot,
  ]);

  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [addingTo, setAddingTo] = useState<TaskTable | null>(null);
  const [editing, setEditing] = useState<{
    table: TaskTable;
    action: ActionEntity;
  } | null>(null);
  const [viewing, setViewing] = useState<ActionEntity | null>(null);

  const generate = useGenerateSectionTasks();
  const pin = usePinSectionTasks(sitrepId, section.id);
  const unlink = useUnlinkSectionTask(sitrepId, section.id);
  const { instantiate, isPending: instantiating } =
    useInstantiateIncidentTask(incidentId);
  const aiTasksEnabled =
    (section.output_format_structured?.ai_tasks as { enabled?: boolean })
      ?.enabled === true;

  const refreshTasks = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: orgActionKeys.base });
    void queryClient.invalidateQueries({ queryKey: incidentActionKeys.base });
    void queryClient.invalidateQueries({ queryKey: sectionTaskLinkKeys.base });
  }, [queryClient]);

  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return;
    lastRefreshKey.current = refreshKey;
    refreshTasks();
  }, [refreshKey, refreshTasks]);

  const pinCreated = useCallback(
    (table: TaskTable, actionId: string) => {
      setHighlighted((current) => new Set(current).add(actionId));
      pin.mutate([{ action_id: actionId, scope: table }], {
        onError: () =>
          toast.error(
            'The task was created but could not be added to this section. It is still in the Tasks tab.'
          ),
      });
    },
    [pin]
  );

  const handleGenerate = () => {
    generate.mutate(
      { sitrepId, sectionId: section.id },
      {
        onSuccess: (result) => {
          const created = result?.created ?? [];
          const skipped = result?.skippedExisting ?? 0;
          setHighlighted(new Set(created.map((task) => task.action_id)));
          const where =
            created[0]?.scope === 'incident' ? 'incident tasks' : 'org tasks';
          const parts = [`Added ${created.length} ${where}`];
          if (skipped > 0) parts.push(`${skipped} already existed`);
          if (result?.failed) parts.push(`${result.failed} could not be saved`);
          if (result?.failed) toast.warning(`${parts.join('. ')}.`);
          else toast.success(`${parts.join('. ')}.`);
          refreshTasks();
        },
        onError: (error) => {
          if (isSitrepAILockedError(error)) {
            toast.error('AI Generate is currently in use — please wait.');
            return;
          }
          toast.error(errorMessage(error, 'Failed to generate tasks.'));
        },
      }
    );
  };

  const handleAdd = (table: TaskTable) => {
    if (table === 'incident' && hwcg) {
      if (instantiating) return;
      void openInstantiatedIncidentTask({
        instantiate,
        status: 'Planned',
        aorId: sitrep?.aor_id,
        onOpen: (row) => {
          pinCreated('incident', String(row.action_id));
          setEditing({ table, action: row as ActionEntity });
        },
      });
      return;
    }
    setAddingTo(table);
  };

  const handleOpen = (table: TaskTable) => (action: ActionEntity) => {
    if (editable) setEditing({ table, action });
    else setViewing(action);
  };

  const renderBadge = (action: ActionEntity) => {
    const link = action.action_id
      ? linkByActionId.get(String(action.action_id))
      : undefined;
    if (!link) return null;
    return (
      <Badge
        variant="outline"
        className="tw:ml-2 tw:h-5 tw:gap-1 tw:px-1.5 tw:text-[10px] tw:font-normal"
        title={
          link.source === 'generated'
            ? 'Generated by this report section'
            : 'Pinned to this report section'
        }
      >
        {link.source === 'generated' ? 'Generated' : 'Pinned'}
        {editable ? (
          <button
            type="button"
            className="tw:inline-flex tw:rounded-sm tw:text-muted-foreground tw:hover:text-foreground"
            aria-label={`Remove ${action.name || 'task'} from this section`}
            title="Remove from this section (the task itself is kept)"
            onClick={(event) => {
              event.stopPropagation();
              unlink.mutate(link.action_id, {
                onError: () =>
                  toast.error('Could not remove the task from this section.'),
              });
            }}
          >
            <X className="tw:size-3" />
          </button>
        ) : null}
      </Badge>
    );
  };

  const busy = generate.isPending;
  const loadErrors = [
    wantsOrg && orgQuery.isError ? 'org tasks' : null,
    wantsIncident && incidentQuery.isError ? 'incident tasks' : null,
  ].filter(Boolean);

  if (tables.length === 0) {
    return (
      <div className={cn('tw:text-sm tw:text-muted-foreground', className)}>
        This section shows incident tasks, but the report is not linked to an
        incident. Change Tasks shown in section settings.
      </div>
    );
  }

  return (
    <div
      className={cn(
        'ReportTasksSection tw:space-y-3 tw:text-foreground',
        className
      )}
      data-testid="report-tasks-section"
    >
      {editable && allowGenerate && aiTasksEnabled ? (
        <div className="tw:flex tw:items-center tw:justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={handleGenerate}
          >
            <Sparkles className="tw:size-4" />
            {busy ? 'Generating tasks…' : 'Generate tasks with AI'}
          </Button>
        </div>
      ) : null}

      {snapshot?.error ? (
        <p role="alert" className="tw:m-0 tw:text-sm tw:text-destructive">
          Tasks could not be loaded when this section was saved.{' '}
          {snapshot.error}
        </p>
      ) : snapshot && !snapshot.complete ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          This snapshot was saved before full task details were kept, so some
          columns are blank.
        </p>
      ) : null}
      {loadErrors.length > 0 ? (
        <div
          role="alert"
          className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-destructive tw:p-2 tw:text-sm"
        >
          <span>Could not load {loadErrors.join(' or ')}.</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={refreshTasks}
          >
            Retry
          </Button>
        </div>
      ) : null}
      {!frozen && linksQuery.isError ? (
        <p role="alert" className="tw:m-0 tw:text-xs tw:text-destructive">
          Could not load which tasks this section generated, so some may be
          missing.
        </p>
      ) : null}

      {tables.map((table) => (
        <div key={table} className="tw:space-y-1">
          {tables.length > 1 ? (
            <h6 className="tw:m-0 tw:text-sm tw:font-semibold">
              {TABLE_TITLES[table]}
            </h6>
          ) : null}
          <ActionsWorkspace
            scope={table === 'incident' ? INCIDENT : ORGANIZATION}
            incidentIdOverride={table === 'incident' ? incidentId : undefined}
            storageKey={`report-tasks-view-${table}`}
            baseActions={selected[table]}
            canEdit={editable}
            readOnly={!editable}
            hiddenToolbarItems={HIDDEN_TOOLBAR}
            isolateAreaFilter
            highlightedActionIds={highlighted}
            renderNameBadge={renderBadge}
            emptyMessage={
              frozen
                ? 'No tasks were in this section when the report was saved.'
                : allowGenerate && aiTasksEnabled
                  ? 'No tasks match this section yet. Add one or generate tasks with AI.'
                  : 'No tasks match this section yet.'
            }
            onEditAction={handleOpen(table)}
            toolbar={
              editable ? (
                <Button
                  type="button"
                  size="sm"
                  disabled={table === 'incident' && instantiating}
                  onClick={() => handleAdd(table)}
                >
                  <Plus className="tw:size-4" />
                  Add Task
                </Button>
              ) : null
            }
          />
        </div>
      ))}

      {addingTo ? (
        <ActionDrawer
          scope={addingTo === 'incident' ? INCIDENT : ORGANIZATION}
          incidentIdOverride={addingTo === 'incident' ? incidentId : undefined}
          defaultAorId={sitrep?.aor_id}
          onCreated={(actionId) => pinCreated(addingTo, actionId)}
          toggle={() => setAddingTo(null)}
        />
      ) : null}
      {editing ? (
        <ActionDrawer
          scope={editing.table === 'incident' ? INCIDENT : ORGANIZATION}
          incidentIdOverride={
            editing.table === 'incident' ? incidentId : undefined
          }
          selectedEntity={editing.action}
          toggle={() => setEditing(null)}
        />
      ) : null}
      <ActionViewModal
        show={!!viewing}
        action={viewing}
        onClose={() => setViewing(null)}
        canEdit={false}
      />
    </div>
  );
}
